import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../app/routes';
import { RiSparklingFill } from 'react-icons/ri';
import {
  FiCheck,
  FiBarChart2,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiLock,
  FiZap,
  FiPhone,
  FiSearch,
  FiMapPin,
  FiRefreshCw,
  FiAlertTriangle,
  FiCopy,
  FiFileText,
  FiSettings,
  FiPlus,
  FiChevronDown,
  FiTrash2,
  FiList,
  FiEye,
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useAuth } from '../features/auth/AuthContext';
import { useAmps } from '../context/AmpContext';
import { useChannels } from '../context/ChannelContext';
import { parseKanbanDate } from '../utils/dateTime';
import CertificatesSidebarPanel from '../features/amps/CertificatesSidebarPanel';
import RateQuoteLinkSidebarPanel from '../features/ratequotelink/RateQuoteLinkSidebarPanel';
import { useRateQuoteLink } from '../features/ratequotelink/RateQuoteLinkContext';
import DocumentPanel from '../components/DocumentPanel';
import type { DocumentItem } from '../components/DocumentPanel';
import * as truckingService from '../services/truckingAppetiteService';
import * as carrierFinderService from '../services/carrierFinderService';
import { useDocumentPanel } from '../context/DocumentPanelContext';
import { useStatusTemplate } from '../context/StatusTemplateContext';
import '../styles/RenewalSidebarPanel.css';
import '../styles/CommandCenterSidebar.css';
import CommandCenterConfigSidebarPanel from '../features/momentumcommand/CommandCenterConfigSidebarPanel';
import DashboardWidgets from '../features/dashboard/DashboardWidgets';
import UpgradeBanner from '../components/ui/UpgradeBanner';
import AddChannelModal from '../features/chat/AddChannelModal';
import CreateAgentPanel from '../features/claudeagents/CreateAgentPanel';
import ConfirmModal from '../features/voice/components/ConfirmModal';
import { generateApiKey } from '../services/apiKeyService';
import type {
  Channel,
  FileAttachment,
  TableData,
} from '../features/chat/chat.types';
import type { Module } from '../features/amps/MarketplacePage';
import OpenClawSidebarPanel from '../features/momentumclaw/OpenClawSidebarPanel';
import InsuredDetailSidebarPanel from '../features/chat/InsuredDetailSidebarPanel';
import PolicyDetailSidebarPanel from '../features/chat/PolicyDetailSidebarPanel';
import { useInsuredDetail } from '../context/InsuredDetailContext';
import { usePolicyDetail } from '../context/PolicyDetailContext';
import { useFeedbackPanel } from '../context/FeedbackPanelContext';
import { useChannelDocuments } from '../context/ChannelDocumentsContext';
import { useRunDetailPanel, type RunDetailKind } from '../context/RunDetailPanelContext';
import RunDetailSidebarPanel from '../features/routines/RunDetailSidebarPanel';
import { useCarrierSession } from '../context/CarrierSessionContext';
import CarrierSessionDetailSidebarPanel from '../features/carrierdownload/CarrierSessionDetailSidebarPanel';
import { uploadChannelFile } from '../services/fileUploadService';
import { apiFetch } from '../utils/apiClient';
import MacWorkflowsSidebarContent from '../features/mac-ai-chat/sidebar';
import DwAgentDashboardSidebarContent from '../features/digitalworkforce/DwAgentDashboardSidebarContent';
import WebhookAnalyticsSidebarContent from '../features/settings/WebhookAnalyticsSidebarContent';
import WebhookCallDetailSidebar from '../features/settings/WebhookCallDetailSidebar';
import ArtifactsSidebarContent from '../features/artifacts/ArtifactsSidebarContent';
import RoutinesSidebarContent from '../features/routines/RoutinesSidebarContent';
import CommunicationSidebarContent from '../features/communication/CommunicationSidebarContent';
import SubmissionCenterSidebarContent from '../features/centers/submission/SubmissionCenterSidebarContent';
import { useSubmissionCenterStats } from '../context/SubmissionCenterStatsContext';
import CarrierDownloadSidebarContent from '../features/carrierdownload/CarrierDownloadSidebarContent';
import VoiceReportsSidebar from '../features/voice/components/VoiceReportsSidebar';
import DialerSidebarContent from '../features/dialer/DialerSidebarContent';
import { useCallDetailPanel } from '../context/CallDetailPanelContext';
import CallDetailSidebarPanel from '../features/voice/components/CallDetailSidebarPanel';
import { useDialerCallDetail } from '../context/DialerCallDetailContext';
import DialerCallDetailSidebarPanel from '../features/dialer/DialerCallDetailSidebarPanel';
import '../styles/ArtifactsSidebar.css';
import '../styles/MomentumClaw.css';
import '../styles/AmpsGridPage.css';
import '../styles/InsuredDetail.css';
import '../styles/SidebarRight.css';

const RUN_DETAIL_TITLE: Record<RunDetailKind, string> = {
  chat: 'Chat Details',
  command: 'Command Details',
  routine: 'Routine Details',
};

// ── Channel Documents Sidebar Panel ──────────────────────────────────────────

const AUTH_URL = process.env.REACT_APP_Auth_Url || '';

interface ChannelFileItem {
  firstName: string;
  lastName: string;
  channelId: number;
  transactionId: string;
  fileName: string;
  created: string;
}

const ChannelDocumentsSidebarPanel: React.FC<{
  channelId: string | null;
  onClose: () => void;
}> = ({ channelId, onClose }) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [pendingFiles, setPendingFiles] = React.useState<File[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [uploadedFiles, setUploadedFiles] = React.useState<
    { name: string; url: string }[]
  >([]);
  const [error, setError] = React.useState<string | null>(null);
  const [channelFiles, setChannelFiles] = React.useState<ChannelFileItem[]>([]);
  const [filesLoading, setFilesLoading] = React.useState(false);
  const [fileDeleteConfirmId, setFileDeleteConfirmId] = React.useState<
    string | null
  >(null);
  const [fileDeletingId, setFileDeletingId] = React.useState<string | null>(
    null,
  );
  const [fileDeleteToast, setFileDeleteToast] = React.useState<string | null>(
    null,
  );
  const [fileViewingId, setFileViewingId] = React.useState<string | null>(null);
  const [fileViewUrl, setFileViewUrl] = React.useState<{
    url: string;
    name: string;
  } | null>(null);

  const fetchChannelFiles = React.useCallback(async () => {
    if (!channelId) return;
    setFilesLoading(true);
    try {
      const res = await apiFetch<ChannelFileItem[]>(
        `${AUTH_URL}/api/momentumamp/channel-files?channelId=${channelId}`,
      );
      const files = Array.isArray(res) ? res : [];
      setChannelFiles(files);
    } catch {
      setChannelFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [channelId]);

  React.useEffect(() => {
    fetchChannelFiles();
  }, [fetchChannelFiles]);

  const deleteChannelFile = React.useCallback(
    async (transactionId: string, fileName?: string) => {
      const authToken = localStorage.getItem('authToken') || '';
      setFileDeleteConfirmId(null);
      setFileDeletingId(transactionId);
      try {
        const formData = new FormData();
        formData.append('transactionId', transactionId);
        const res = await fetch(
          `${AUTH_URL}/api/momentumamp/insurance-agency-file`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${authToken}` },
            body: formData,
          },
        );
        if (!res.ok) throw new Error();
        setChannelFiles((prev) =>
          prev.filter((f) => f.transactionId !== transactionId),
        );
        const label = fileName ? `"${fileName}" deleted` : 'File deleted';
        setFileDeleteToast(label);
        setTimeout(() => setFileDeleteToast(null), 3500);
      } catch {
        fetchChannelFiles();
      } finally {
        setFileDeletingId(null);
      }
    },
    [fetchChannelFiles],
  );

  const viewChannelFile = React.useCallback(async (f: ChannelFileItem) => {
    const authToken = localStorage.getItem('authToken') || '';
    setFileViewingId(f.transactionId);
    try {
      const params = new URLSearchParams({
        transactionId: f.transactionId,
        channelId: String(f.channelId),
        fileName: f.fileName,
      });
      const res = await fetch(
        `${AUTH_URL}/api/momentumamp/insurance-agency-file?${params.toString()}`,
        { method: 'GET', headers: { Authorization: `Bearer ${authToken}` } },
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const json = await res.json();
      const fileUrl = json?.fileUrl;
      if (fileUrl) setFileViewUrl({ url: fileUrl, name: f.fileName });
    } catch (e) {
      console.error('[viewChannelFile] error:', e);
    } finally {
      setFileViewingId(null);
    }
  }, []);

  const stageFiles = React.useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setPendingFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      return [...prev, ...arr.filter((f) => !existingNames.has(f.name))];
    });
    setError(null);
  }, []);

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      stageFiles(e.dataTransfer.files);
    },
    [stageFiles],
  );

  const removePending = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = React.useCallback(async () => {
    if (pendingFiles.length === 0 || uploading) return;
    if (!channelId) {
      setError(
        'Workspace not identified. Please re-open Documents from a workspace page.',
      );
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const results = await Promise.all(
        pendingFiles.map((f) => uploadChannelFile(f, channelId)),
      );
      setUploadedFiles((prev) => [
        ...prev,
        ...results.map((r) => ({ name: r.fileName, url: r.fileUrl })),
      ]);
      setPendingFiles([]);
      fetchChannelFiles();
    } catch (e: any) {
      setError(e.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [pendingFiles, uploading, channelId, fetchChannelFiles]);

  return (
    <div className="channel-docs__root">
      <div className="channel-docs__body">
        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className="channel-docs__dropzone"
          style={{
            border: `2px dashed ${dragOver ? 'rgba(162,0,255,0.7)' : 'var(--border-glass, rgba(255,255,255,0.12))'}`,
            cursor: uploading ? 'default' : 'pointer',
            background: dragOver
              ? 'rgba(162,0,255,0.06)'
              : 'rgba(255,255,255,0.02)',
          }}
        >
          <FiFileText size={22} className="channel-docs__dropzone-icon" />
          <span className="channel-docs__dropzone-title">
            Click or drag files here
          </span>
          <span className="channel-docs__dropzone-subtitle">
            PDF, Word, Excel, images and more
          </span>
        </div>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          className="channel-docs__file-input"
          accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.json'
          onChange={(e) => {
            if (e.target.files) stageFiles(e.target.files);
            e.target.value = '';
          }}
        />

        {/* Pending files */}
        {pendingFiles.length > 0 && (
          <div className="channel-docs__file-group">
            <span className="channel-docs__group-label">
              Selected — {pendingFiles.length} file
              {pendingFiles.length !== 1 ? 's' : ''}
            </span>
            {pendingFiles.map((f, i) => (
              <div key={i} className="channel-docs__pending-item">
                <FiFileText size={13} className="channel-docs__pending-icon" />
                <span className="channel-docs__file-name">
                  {f.name}
                </span>
                {!uploading && (
                  <button
                    title='Remove'
                    onClick={() => removePending(i)}
                    className="channel-docs__remove-btn"
                  >
                    <FiX size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="channel-docs__error">
            <FiAlertTriangle
              size={13}
              className="channel-docs__error-icon"
            />{' '}
            {error}
          </div>
        )}

        {/* Uploaded files — session */}
        {uploadedFiles.length > 0 && (
          <div className="channel-docs__file-group">
            <span className="channel-docs__group-label">
              Uploaded
            </span>
            {uploadedFiles.map((f, i) => (
              <div key={i} className="channel-docs__uploaded-item">
                <FiCheck
                  size={12}
                  className="channel-docs__uploaded-icon"
                />
                <span className="channel-docs__file-name">
                  {f.name}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Channel files — from API */}
        {(filesLoading || channelFiles.length > 0) && (
          <div className="channel-docs__file-group">
            <div className="channel-docs__group-header">
              <span className="channel-docs__group-label">
                Workspace Files{' '}
                {!filesLoading &&
                  channelFiles.length > 0 &&
                  `(${channelFiles.length})`}
              </span>
              {!filesLoading && (
                <button
                  onClick={fetchChannelFiles}
                  title='Refresh'
                  className="channel-docs__refresh-btn"
                >
                  <FiRefreshCw size={11} />
                </button>
              )}
            </div>
            {filesLoading ? (
              <div className="channel-docs__loading-row">
                <FiRefreshCw
                  size={13}
                  className="sidebar-right__spin-icon channel-docs__loading-icon"
                />
                Loading files…
              </div>
            ) : (
              channelFiles.map((f, i) => (
                <div
                  key={f.transactionId ?? i}
                  className="channel-docs__file-card"
                >
                  {/* Row 1 — icon + full-width filename */}
                  <div className="channel-docs__file-row">
                    <FiFileText
                      size={12}
                      className="channel-docs__file-icon"
                    />
                    <span className="channel-docs__file-name">
                      {f.fileName}
                    </span>
                  </div>
                  {/* Row 2 — meta + action buttons */}
                  <div className="channel-docs__file-meta-row">
                    <span className="channel-docs__file-meta">
                      {f.firstName} {f.lastName} ·{' '}
                      {new Date(f.created).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    <div className="channel-docs__file-actions">
                      <button
                        onClick={() => viewChannelFile(f)}
                        disabled={fileViewingId === f.transactionId}
                        className="channel-docs__view-btn"
                      >
                        {fileViewingId === f.transactionId ? (
                          <FiRefreshCw
                            size={9}
                            className="sidebar-right__spin-icon"
                          />
                        ) : (
                          'View'
                        )}
                      </button>
                      <button
                        onClick={() => setFileDeleteConfirmId(f.transactionId)}
                        disabled={fileDeletingId === f.transactionId}
                        className="channel-docs__file-delete-btn"
                        onMouseEnter={(e) => {
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.background = 'rgba(239,68,68,0.12)';
                          (e.currentTarget as HTMLButtonElement).style.color =
                            '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                          (
                            e.currentTarget as HTMLButtonElement
                          ).style.background = 'transparent';
                          (e.currentTarget as HTMLButtonElement).style.color =
                            '#94a3b8';
                        }}
                      >
                        {fileDeletingId === f.transactionId ? (
                          <FiRefreshCw
                            size={10}
                            className="sidebar-right__spin-icon"
                          />
                        ) : (
                          <FiTrash2 size={11} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="channel-docs__footer">
        {pendingFiles.length > 0 && (
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="channel-docs__btn channel-docs__btn--upload"
            style={{ opacity: uploading ? 0.7 : 1 }}
          >
            {uploading ? (
              <>
                <FiRefreshCw
                  size={13}
                  className="sidebar-right__spin-icon"
                />{' '}
                Uploading…
              </>
            ) : (
              <>
                <FiPlus size={13} /> Upload {pendingFiles.length} file
                {pendingFiles.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        )}
        <button
          onClick={onClose}
          className="channel-docs__btn channel-docs__btn--close"
        >
          Close
        </button>
      </div>

      {/* ── Delete confirm modal (portalled to body to escape overflow:hidden) ── */}
      {fileDeleteConfirmId &&
        ReactDOM.createPortal(
          <div
            className="channel-docs__modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setFileDeleteConfirmId(null);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="channel-docs__modal"
            >
              <div className="channel-docs__modal-header">
                <div className="channel-docs__modal-header-left">
                  <div className="channel-docs__modal-icon">
                    <FiTrash2 size={14} />
                  </div>
                  <div>
                    <div className="channel-docs__modal-title">
                      Delete File
                    </div>
                    <div className="channel-docs__modal-subtitle">
                      This action cannot be undone
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setFileDeleteConfirmId(null)}
                  className="channel-docs__modal-close-btn"
                >
                  <FiX size={15} />
                </button>
              </div>
              <div className="channel-docs__modal-body">
                Are you sure you want to delete{' '}
                <strong>
                  "
                  {
                    channelFiles.find(
                      (f) => f.transactionId === fileDeleteConfirmId,
                    )?.fileName
                  }
                  "
                </strong>
                ? It will be permanently removed.
              </div>
              <div className="channel-docs__modal-footer">
                <button
                  onClick={() => setFileDeleteConfirmId(null)}
                  className="channel-docs__modal-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    deleteChannelFile(
                      fileDeleteConfirmId,
                      channelFiles.find(
                        (f) => f.transactionId === fileDeleteConfirmId,
                      )?.fileName,
                    )
                  }
                  className="channel-docs__modal-delete-btn"
                >
                  <FiTrash2 size={11} /> Delete
                </button>
              </div>
            </motion.div>
          </div>,
          document.body,
        )}

      {/* ── File viewer modal (portalled to body) ── */}
      {fileViewUrl &&
        ReactDOM.createPortal(
          <div
            className="channel-docs__viewer-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) setFileViewUrl(null);
            }}
          >
            <div className="channel-docs__viewer-modal">
              {/* Header */}
              <div className="channel-docs__viewer-header">
                <div className="channel-docs__viewer-header-left">
                  <div className="channel-docs__viewer-icon">
                    <FiFileText size={13} />
                  </div>
                  <span className="channel-docs__viewer-title">
                    {fileViewUrl.name}
                  </span>
                </div>
                <div className="channel-docs__viewer-header-actions">
                  <a
                    href={fileViewUrl.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className="channel-docs__viewer-open-link"
                  >
                    Open in tab
                  </a>
                  <button
                    onClick={() => setFileViewUrl(null)}
                    className="channel-docs__viewer-close-btn"
                  >
                    <FiX size={16} />
                  </button>
                </div>
              </div>
              {/* iframe */}
              <iframe
                src={fileViewUrl.url}
                title={fileViewUrl.name}
                className="channel-docs__viewer-iframe"
              />
            </div>
          </div>,
          document.body,
        )}

      {/* ── Delete success toast (portalled to body) ── */}
      {fileDeleteToast &&
        ReactDOM.createPortal(
          <motion.div
            initial={{ opacity: 0, y: -16, x: 16 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            className="channel-docs__toast"
          >
            <FiCheck size={14} />
            {fileDeleteToast}
          </motion.div>,
          document.body,
        )}
    </div>
  );
};

// ── Commission Scan Sidebar Panel ─────────────────────────────────────────────

const AMS_BASE = process.env.REACT_APP_Auth_Url || '';

const CommissionScanSidebarPanel: React.FC<{
  pdf: FileAttachment;
  table: TableData;
  conversationId: string;
}> = ({ pdf, table, conversationId }) => {
  const [importing, setImporting] = React.useState(false);
  const [importSuccess, setImportSuccess] = React.useState(false);
  const [importError, setImportError] = React.useState<string | null>(null);

  const handleImport = async () => {
    setImporting(true);
    setImportError(null);
    setImportSuccess(false);
    try {
      const authToken = localStorage.getItem('authToken') || '';
      const amsToken = localStorage.getItem('amsToken') || '';
      const res = await fetch(
        `${AMS_BASE}/api/MomentumAMS/upload/${conversationId}?token=${encodeURIComponent(amsToken)}`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      if (!res.ok) throw new Error(`Import failed (${res.status})`);
      const data = await res.json();
      if (data.redirectURL)
        window.open(data.redirectURL, '_blank', 'noopener,noreferrer');
      setImportSuccess(true);
    } catch (e: any) {
      setImportError(e.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="commission-scan__root">
      {/* Import button */}
      <div className="commission-scan__import-bar">
        <motion.button
          className='glass-btn glass-btn-primary commission-scan__import-btn'
          onClick={handleImport}
          disabled={importing || importSuccess}
          whileHover={{ scale: importing || importSuccess ? 1 : 1.02 }}
          whileTap={{ scale: importing || importSuccess ? 1 : 0.97 }}
          style={{
            opacity: importing || importSuccess ? 0.8 : 1,
          }}
        >
          {importing ? (
            <>
              <FiRefreshCw
                size={13}
                className="sidebar-right__spin-icon"
              />{' '}
              Importing…
            </>
          ) : importSuccess ? (
            <>
              <FiCheck size={13} /> Imported to AMS
            </>
          ) : (
            'Import Commission'
          )}
        </motion.button>
        {importError && (
          <div className="commission-scan__import-error">
            <FiAlertTriangle size={11} /> {importError}
          </div>
        )}
      </div>

      {/* File preview */}
      <div className="commission-scan__preview">
        {(() => {
          const ext = (pdf.name || '').split('.').pop()?.toLowerCase() || '';
          const isImage =
            ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext) ||
            (pdf.type || '').startsWith('image/');
          const isFrameable =
            ext === 'pdf' ||
            pdf.type === 'application/pdf' ||
            ['csv', 'txt', 'md', 'json', 'xml'].includes(ext) ||
            (pdf.type || '').startsWith('text/');

          if (pdf.fileUrl && isImage) {
            return (
              <img
                src={pdf.fileUrl}
                alt={pdf.name}
                className="commission-scan__preview-img"
              />
            );
          }
          if (pdf.fileUrl && isFrameable) {
            return (
              <iframe
                src={pdf.fileUrl}
                title={pdf.name}
                className="commission-scan__preview-iframe"
              />
            );
          }
          const Icon = ['xlsx', 'xls'].includes(ext) ? FiBarChart2 : FiFileText;
          return (
            <div className="commission-scan__preview-fallback">
              <Icon size={32} />
              <span className="commission-scan__preview-filename">
                {pdf.name}
              </span>
              {ext && (
                <span className="commission-scan__preview-ext">
                  {ext} file
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Extracted data table */}
      <div className="commission-scan__table-section">
        <div className="commission-scan__table-label">
          Extracted Data
        </div>
        <div className="commission-scan__table-scroll">
          <table className="commission-scan__table">
            <thead>
              <tr>
                {table.headers.map((h, i) => (
                  <th key={i} className="commission-scan__th">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, i) => (
                <tr key={i} className="commission-scan__tr">
                  {row.map((cell, j) => (
                    <td key={j} className="commission-scan__td">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Tool Log panel (rendered inside right sidebar) ────────────────────────────
interface ToolLogEntry {
  id: string;
  name: string;
  input: Record<string, unknown>;
  status: 'running' | 'pending' | 'approved' | 'denied' | 'done';
  calledAt: number;
  completedAt?: number;
  mcpServer?: string;
}

const SidebarToolLogPanel: React.FC<{
  entries: ToolLogEntry[];
  onClose: () => void;
}> = ({ entries, onClose }) => {
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const fmt = (ts: number) =>
    new Date(ts).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  const latency = (e: ToolLogEntry) =>
    e.completedAt
      ? `${((e.completedAt - e.calledAt) / 1000).toFixed(2)}s`
      : '…';
  const statusColor = (s: string) =>
    s === 'done' || s === 'approved'
      ? '#4ade80'
      : s === 'denied'
        ? '#f87171'
        : s === 'running'
          ? '#fb923c'
          : '#a78bfa';

  return (
    <div className="tool-log__root">
      {entries.length === 0 ? (
        <p className="tool-log__empty">
          No tool calls yet in this session.
        </p>
      ) : (
        <div className="tool-log__list">
          {[...entries].reverse().map((e) => (
            <div key={e.id} className="tool-log__entry">
              <div
                className="tool-log__entry-header"
                style={{
                  cursor:
                    Object.keys(e.input).length > 0 ? 'pointer' : 'default',
                }}
                onClick={() =>
                  Object.keys(e.input).length > 0 &&
                  setExpanded((ex) => (ex === e.id ? null : e.id))
                }
              >
                <span
                  className="tool-log__status-dot"
                  style={{ background: statusColor(e.status) }}
                />
                <span className="tool-log__entry-name">
                  {e.name
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </span>
                <span className="tool-log__entry-time">
                  {fmt(e.calledAt)}
                </span>
                <span className="tool-log__entry-latency">
                  {latency(e)}
                </span>
                {Object.keys(e.input).length > 0 && (
                  <FiChevronDown
                    size={10}
                    className="tool-log__entry-chevron"
                    style={{
                      transform: expanded === e.id ? 'rotate(180deg)' : 'none',
                    }}
                  />
                )}
              </div>
              {e.mcpServer && (
                <div className="tool-log__mcp-server">
                  {e.mcpServer}
                </div>
              )}
              {expanded === e.id && Object.keys(e.input).length > 0 && (
                <div className="tool-log__expanded">
                  {Object.entries(e.input).map(([k, v]) => (
                    <div key={k} className="tool-log__input-row">
                      <span className="tool-log__input-key">
                        {k}
                      </span>
                      <span className="tool-log__input-value">
                        {typeof v === 'object'
                          ? JSON.stringify(v, null, 2)
                          : String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface SidebarRightProps {
  isOpen: boolean;
  onToggle: () => void;
  onToggleMainPanel?: () => void;
  mainCollapsed?: boolean;
  showCreateAgent?: boolean;
  onCloseCreateAgent?: () => void;
  showAddChannel?: boolean;
  onCloseAddChannel?: () => void;
  onAddChannelSubmit?: (
    channelData: Omit<Channel, 'id' | 'guid' | 'createdAt'>,
  ) => void;
  editingChannel?: Channel;
  onEditChannelSubmit?: (
    channelId: string,
    updates: Omit<Channel, 'id' | 'guid' | 'createdAt'>,
  ) => void;
  marketplaceModule?: Module | null;
  onMarketplaceInstall?: () => Promise<void>;
  onMarketplaceUninstall?: () => Promise<void>;
  onMarketplaceToggle?: (isEnabled: boolean) => Promise<void>;
  marketplaceIsProcessing?: boolean;
  onCloseMarketplaceDetail?: () => void;
  // Create API key panel
  showCreateApiKey?: boolean;
  onCloseCreateApiKey?: () => void;
  onApiKeyCreated?: () => void;
  // Commission scan panel
  commissionScanData?: {
    pdf: FileAttachment;
    table: TableData;
    conversationId: string;
  } | null;
  onCloseCommissionScan?: () => void;
  // OpenClaw panel (Momentum Claw)
  showOpenClawPanel?: boolean;
  openClawConnected?: boolean;
  // Add Claw Skill panel
  showAddClawSkill?: boolean;
  onCloseAddClawSkill?: () => void;
  onClawSkillCreated?: () => void;
  editClawSkillData?: { name: string; skill: string } | null;
  // Insured Detail panel
  insuredDetailId?: string | null;
  onCloseInsuredDetail?: () => void;
  // Tool Log panel
  showToolLog?: boolean;
  toolLogEntries?: unknown[];
  onCloseToolLog?: () => void;
}

// ── Marketplace pricing panel ─────────────────────────────────────────────

function pricingLabel(pricing: string): string {
  if (pricing.includes('/run')) return 'Pay Per Run';
  if (pricing.includes('/mo')) return 'Monthly Subscription';
  if (pricing.includes('/min')) return 'Pay Per Minute';
  if (pricing.includes('/page')) return 'Pay Per Page';
  if (pricing.includes('/cert')) return 'Pay Per Certificate';
  if (pricing.includes('/lookup')) return 'Pay Per Lookup';
  if (pricing.includes('/comparison')) return 'Pay Per Comparison';
  return 'Pricing';
}

function fmtRunDetailDate(iso: string): string {
  const d = parseKanbanDate(iso);
  if (!d) {
    const raw = (iso || '').trim();
    return raw ? raw.replace('•', '·') : '';
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' · ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

const CATEGORY_COLORS: Record<string, string> = {
  Scanning: '#10b981',
  Support: '#22d3ee',
  Policy: '#f59e0b',
  Data: '#8b5cf6',
  Tools: '#ec4899',
};

const MarketplaceSidebarPanel: React.FC<{
  module: Module;
  onInstall?: () => Promise<void>;
  onUninstall?: () => Promise<void>;
  isProcessing?: boolean;
}> = ({
  module,
  onInstall: _onInstall,
  onUninstall,
  isProcessing: _isProcessing,
}) => {
  // const _isInstalled = module.installed;
  const [showUninstallConfirm, setShowUninstallConfirm] = useState(false);

  return (
    <>
      {showUninstallConfirm && (
        <ConfirmModal
          title='Uninstall Module'
          message={`Are you sure you want to uninstall "${module.name}"? This will remove it from your workspace.`}
          confirmLabel='Uninstall'
          danger
          onConfirm={() => {
            setShowUninstallConfirm(false);
            onUninstall?.();
          }}
          onCancel={() => setShowUninstallConfirm(false)}
        />
      )}
      <div className="marketplace-panel__root">
        {/* Pricing card */}
        <div className="marketplace-panel__card marketplace-panel__card--pricing">
          <span className="marketplace-panel__pricing-value">
            {module.pricing}
          </span>
          <span className="marketplace-panel__pricing-label">
            {pricingLabel(module.pricing)}
          </span>

          {/* Install / Installed / Uninstall — hidden for now
        {isInstalled ? (
          <div
            style={{
              width: '100%',
              padding: '9px 0',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              background: 'rgba(52,211,153,0.12)',
              border: '1px solid rgba(52,211,153,0.4)',
              color: 'var(--accent-green, #34d399)',
            }}
          >
            <FiCheck size={14} />
            Installed
          </div>
        ) : (
          <motion.button
            onClick={onInstall}
            disabled={isProcessing}
            whileHover={{ scale: isProcessing ? 1 : 1.02 }}
            whileTap={{ scale: isProcessing ? 1 : 0.97 }}
            style={{
              width: '100%',
              padding: '9px 0',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              background: 'rgba(34,211,238,0.12)',
              border: '1px solid rgba(34,211,238,0.4)',
              color: 'var(--accent-cyan, #22d3ee)',
              opacity: isProcessing ? 0.6 : 1,
            }}
          >
            {isProcessing ? 'Installing…' : 'Install'}
          </motion.button>
        )}

        {isInstalled && (
          <motion.button
            onClick={() => setShowUninstallConfirm(true)}
            disabled={isProcessing}
            whileHover={{ scale: isProcessing ? 1 : 1.02 }}
            whileTap={{ scale: isProcessing ? 1 : 0.97 }}
            style={{
              width: '100%',
              padding: '7px 0',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 500,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              background: 'transparent',
              border: '1px solid rgba(239,68,68,0.3)',
              color: 'var(--accent-red, #ef4444)',
              opacity: isProcessing ? 0.6 : 1,
            }}
          >
            <FiX size={13} />
            {isProcessing ? 'Removing…' : 'Uninstall'}
          </motion.button>
        )}
        */}
        </div>

        {/* Publisher card */}
        <div className="marketplace-panel__card">
          <span className="marketplace-panel__section-label">
            Publisher
          </span>
          <div className="marketplace-panel__row">
            <div className="marketplace-panel__avatar">
              M
            </div>
            <div>
              <div className="marketplace-panel__publisher-name">
                {module.publisher}
              </div>
              <div className="marketplace-panel__verified-row">
                <FiCheck size={11} />
                Verified Publisher
              </div>
            </div>
          </div>
        </div>

        {/* Trust & Compliance card */}
        <div className="marketplace-panel__card">
          <span className="marketplace-panel__section-label">
            Trust & Compliance
          </span>
          <div className="marketplace-panel__trust-list">
            <div className="marketplace-panel__trust-item">
              <FiLock
                size={14}
                className="marketplace-panel__trust-icon"
              />
              <span className="marketplace-panel__trust-text">
                Verified
              </span>
            </div>
            <div className="marketplace-panel__trust-item">
              <FiCheck
                size={14}
                className="marketplace-panel__trust-icon"
              />
              <span className="marketplace-panel__trust-text">
                Compliant
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Create API Key Sidebar Panel ──────────────────────────────────────────────

const CreateApiKeySidebarPanel: React.FC<{
  onClose: () => void;
  onKeyCreated: () => void;
}> = ({ onClose, onKeyCreated }) => {
  const [keyName, setKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    if (!keyName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const response = await generateApiKey(keyName.trim());
      if (response.data?.apiKey) {
        setCreatedKey(response.data.apiKey);
        onKeyCreated();
      } else {
        setError('Something went wrong. Please try again after sometime.');
      }
    } catch {
      setError('Something went wrong. Please try again after sometime.');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* fallback */
    }
  };

  return (
    <div className="create-api-key__root">
      {/* Panel header removed — sidebar header shows title */}

      {!createdKey ? (
        <>
          {/* Key name input */}
          <div className="create-api-key__field">
            <label className="create-api-key__label">
              Key Name
            </label>
            <input
              className='glass-input create-api-key__input'
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder='e.g. production-app'
              autoFocus
              onKeyDown={(e) =>
                e.key === 'Enter' && !creating && handleCreate()
              }
            />
            <p className="create-api-key__hint">
              Choose a descriptive name to identify where this key is used.
            </p>
          </div>

          {error && (
            <div className="create-api-key__error">
              <FiAlertTriangle size={12} /> {error}
            </div>
          )}

          {/* Actions */}
          <div className="create-api-key__actions">
            <button
              className='glass-btn create-api-key__cancel-btn'
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className='glass-btn create-api-key__create-btn'
              onClick={handleCreate}
              disabled={creating || !keyName.trim()}
              style={{ opacity: creating || !keyName.trim() ? 0.6 : 1 }}
            >
              {creating ? (
                <>
                  <span
                    className='profile-loader create-api-key__loader'
                  />{' '}
                  Creating…
                </>
              ) : (
                'Create Key'
              )}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Created key display */}
          <div className="create-api-key__key-display">
            <code className="create-api-key__key-code">
              {createdKey}
            </code>
            <button
              onClick={handleCopy}
              className="create-api-key__copy-btn"
              style={{
                background: copied
                  ? 'rgba(52,211,153,0.12)'
                  : 'rgba(34,211,238,0.1)',
                border: `1px solid ${copied ? 'rgba(52,211,153,0.3)' : 'rgba(34,211,238,0.25)'}`,
                color: copied
                  ? 'var(--accent-green, #34d399)'
                  : 'var(--accent-cyan)',
              }}
            >
              {copied ? <FiCheck size={11} /> : <FiCopy size={11} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          {/* Warning */}
          <div className="create-api-key__warning">
            <FiAlertTriangle
              size={12}
              className="create-api-key__warning-icon"
            />
            <span className="create-api-key__warning-text">
              Store this key securely. It will not be shown again after you
              close this panel.
            </span>
          </div>

          <button
            className='glass-btn create-api-key__done-btn'
            onClick={onClose}
          >
            I've saved it — Close
          </button>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const SidebarRight: React.FC<SidebarRightProps> = ({
  isOpen,
  onToggle,
  onToggleMainPanel,
  mainCollapsed,
  showCreateAgent = false,
  onCloseCreateAgent,
  showAddChannel = false,
  onCloseAddChannel,
  onAddChannelSubmit,
  editingChannel,
  onEditChannelSubmit,
  marketplaceModule,
  onMarketplaceInstall,
  onMarketplaceUninstall,
  marketplaceIsProcessing,
  onCloseMarketplaceDetail,
  showCreateApiKey = false,
  onCloseCreateApiKey,
  onApiKeyCreated,
  commissionScanData,
  onCloseCommissionScan,
  showOpenClawPanel = false,
  openClawConnected = true,
  showAddClawSkill = false,
  onCloseAddClawSkill,
  onClawSkillCreated,
  editClawSkillData,
  insuredDetailId,
  onCloseInsuredDetail,
  showToolLog = false,
  toolLogEntries = [],
  onCloseToolLog,
}) => {
  const showMarketplace = !!marketplaceModule;
  const showCommissionScan = !!commissionScanData;
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { amps } = useAmps();
  const { getChannel } = useChannels();

  // Detect Certificates AMP — works on both /amp/:id and /channel/:guid routes
  const ampRouteMatch = location.pathname.match(/^\/amp\/([\w:.-]+)/);
  const currentAmpId = ampRouteMatch ? ampRouteMatch[1] : null;
  const currentAmp = currentAmpId
    ? amps.find((a) => a.id === currentAmpId)
    : null;

  const channelRouteMatch = location.pathname.match(/^\/channel\/([\w-]+)/);
  const channelGuidFromRoute = channelRouteMatch ? channelRouteMatch[1] : null;
  const channelObj = channelGuidFromRoute
    ? getChannel(channelGuidFromRoute)
    : null;
  const ampIdFromQuery = new URLSearchParams(location.search).get('amp');
  const channelActiveAmpId =
    ampIdFromQuery || (channelObj?.ampIds?.[0] ?? null);
  const channelCurrentAmp = channelActiveAmpId
    ? amps.find((a) => a.id === channelActiveAmpId)
    : null;

  const isCertificatesActive =
    currentAmp?.name === 'Certificates' ||
    channelCurrentAmp?.name === 'Certificates';
  const isRateQuoteLinkActive =
    currentAmp?.category === 'Rate Quoting Skill' ||
    channelCurrentAmp?.category === 'Rate Quoting Skill' ||
    currentAmp?.name === 'Rate Quoting Skill' ||
    channelCurrentAmp?.name === 'Rate Quoting Skill' ||
    location.pathname.startsWith('/rate-quote-link') ||
    location.pathname.startsWith('/rate-quoting');
  const noOtherPanel =
    !showCommissionScan &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill &&
    !showCreateAgent &&
    !showToolLog;
  const showCertificates = isCertificatesActive && noOtherPanel;
  const showRateQuoteLink = isRateQuoteLinkActive && noOtherPanel;

  // ── Kanban Configuration Panel ──
  const isKanbanPage = location.pathname.startsWith('/momentum-kanban');
  // ── MAC Workflows page ──
  const isMacWorkflowsPage = location.pathname.startsWith('/mac-workflows');

  // ── Agent Dashboard page ──
  const isDwArtifactsPage = location.pathname === '/dw-artifacts' || location.pathname === '/digital-workforce';
  const isArtifactsPage   = location.pathname.startsWith('/artifacts');
  const isRoutinesPage    = location.pathname.startsWith(ROUTES.MOMENTUM_ROUTINES);
  const isCarrierDownloadPage = location.pathname.startsWith(ROUTES.CARRIER_DOWNLOAD);
  const isVoicePage = location.pathname.startsWith('/voice');
  const isDialerPage = location.pathname.startsWith(ROUTES.DIALER);
  const isCommunicationCenterPage = location.pathname.startsWith(ROUTES.COMMUNICATION_CENTER);
  const isSubmissionCenterPage = location.pathname.startsWith(ROUTES.CENTER_SUBMISSION_TRACKER);
  const isWebhooksPage = location.pathname.startsWith(ROUTES.SETTINGS_WEBHOOKS);
  // A call picked on /settings/webhooks/report (?call=<id>) replaces the analytics panel.
  const webhookCallId = isWebhooksPage ? Number(new URLSearchParams(location.search).get('call')) || null : null;
  const closeWebhookCall = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('call');
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // ── Document Panel for Trucking / Carrier Finder ──
  const isTruckingPage = location.pathname.startsWith('/trucking-appetite');
  const isCarrierFinderPage = location.pathname.startsWith('/carrier-finder');
  const { showDocPanel, setShowDocPanel } = useDocumentPanel();
  const showDocumentPanel =
    showDocPanel && (isTruckingPage || isCarrierFinderPage) && noOtherPanel;

  const [docPanelDocs, setDocPanelDocs] = useState<DocumentItem[]>([]);
  const [docPanelLoading, setDocPanelLoading] = useState(false);
  const docLoadingRef = useRef(false);

  const loadDocPanelDocs = useCallback(async () => {
    if (docLoadingRef.current) return; // prevent duplicate calls
    docLoadingRef.current = true;
    setDocPanelLoading(true);
    try {
      if (location.pathname.startsWith('/trucking-appetite')) {
        const docs = await truckingService.getDocuments();
        setDocPanelDocs(
          docs.map((d: any) => ({
            id: d.id || d.Id,
            fileName: d.fileName || d.FileName || d.filename || '',
            createdDate: d.createdDate || d.CreatedDate || d.created_date || '',
          })),
        );
      } else if (location.pathname.startsWith('/carrier-finder')) {
        const files = await carrierFinderService.getFiles();
        setDocPanelDocs(
          files.map((f: any) => ({
            id: f.id || f.Id,
            fileName: f.fileName || f.FileName || f.filename || '',
            createdDate: f.createdDate || f.CreatedDate || f.created_date || '',
            fileUrl: f.fileUrl || '',
          })),
        );
      }
    } catch {
      /* ignore */
    } finally {
      setDocPanelLoading(false);
      docLoadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Reset doc panel when navigating away
  useEffect(() => {
    if (!isTruckingPage && !isCarrierFinderPage) {
      setShowDocPanel(false);
      setDocPanelDocs([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTruckingPage, isCarrierFinderPage]);

  // Auto-expand the sidebar when Certificates AMP is active
  useEffect(() => {
    if (showCertificates && !isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCertificates]);

  // Auto-expand the sidebar when navigating to Momentum Voice
  useEffect(() => {
    if (isVoicePage && !isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVoicePage]);

  // Auto-expand the sidebar when navigating to the Dialer
  useEffect(() => {
    if (isDialerPage && !isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialerPage]);

  // Rate Quoting Skill — auto-expand the workspace whenever the page
  // requests it (Settings click, row click, Execute click).
  const { openSignal: rqlOpenSignal, closeSignal: rqlCloseSignal } =
    useRateQuoteLink();
  useEffect(() => {
    if (rqlOpenSignal > 0 && isRateQuoteLinkActive && !isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rqlOpenSignal]);
  useEffect(() => {
    if (rqlCloseSignal > 0 && isRateQuoteLinkActive && isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rqlCloseSignal]);

  // Listen for doc panel open event — use refs to avoid stale closures
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;
  const loadDocPanelDocsRef = useRef(loadDocPanelDocs);
  loadDocPanelDocsRef.current = loadDocPanelDocs;

  useEffect(() => {
    const handleOpen = () => {
      const path = window.location.pathname;
      if (
        path.startsWith('/trucking-appetite') ||
        path.startsWith('/carrier-finder')
      ) {
        if (!isOpenRef.current) {
          onToggleRef.current();
        }
        loadDocPanelDocsRef.current();
      }
    };
    window.addEventListener('docpanel:open', handleOpen);
    return () => window.removeEventListener('docpanel:open', handleOpen);
  }, []);
  const { statusTemplate } = useStatusTemplate();
  const { selectedInsuredId, setSelectedInsuredId } = useInsuredDetail();
  const { selectedPolicyId, setSelectedPolicyId } = usePolicyDetail();
  const { feedbackPanel, setFeedbackPanel } = useFeedbackPanel();
  const { showChannelDocPanel, channelDocChannelId, closeChannelDocPanel } =
    useChannelDocuments();
  const { runDetailPanel, setRunDetailPanel } = useRunDetailPanel();
  const { callDetailPanel, setCallDetailPanel } = useCallDetailPanel();
  const { dialerCallDetail, setDialerCallDetail } = useDialerCallDetail();
  const { selected: submissionSelected, setSelected: setSubmissionSelected } = useSubmissionCenterStats();
  const showSubmissionQuoteDetail = isSubmissionCenterPage && !!submissionSelected;
  // Picking a quote should surface its detail — if the rail is currently
  // collapsed, open it rather than silently updating hidden content.
  useEffect(() => {
    if (showSubmissionQuoteDetail && !isOpen) onToggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSubmissionQuoteDetail]);
  const { sessionPanel, closeSession } = useCarrierSession();

  // Auto-expand the sidebar when a run detail panel is opened
  useEffect(() => {
    if (runDetailPanel && !isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runDetailPanel]);

  // Auto-expand the sidebar when a call detail panel is opened
  useEffect(() => {
    if (callDetailPanel && !isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callDetailPanel]);

  // Auto-expand the sidebar when a Dialer call log detail is opened
  useEffect(() => {
    if (dialerCallDetail && !isOpen) {
      onToggle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialerCallDetail]);

  const activeInsuredId = insuredDetailId || selectedInsuredId;
  const activeInsuredOrPolicy = !!(selectedInsuredId || selectedPolicyId);
  const showKanbanConfig = isKanbanPage && noOtherPanel;

  const showAddMcpPanel =
    feedbackPanel?.type === 'add-mcp' &&
    !showCommissionScan &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill;
  const showViewMcpPanel =
    feedbackPanel?.type === 'view-mcp' &&
    !showCommissionScan &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill;
  const showPolicyDetail =
    !!selectedPolicyId &&
    !showAddMcpPanel &&
    !showViewMcpPanel &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill &&
    !showCommissionScan;
  // Picking a policy/quote should surface its detail — if the rail is
  // currently collapsed, open it rather than silently updating hidden content.
  useEffect(() => {
    if (showPolicyDetail && !isOpen) onToggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPolicyDetail]);
  const showInsuredDetail =
    !!activeInsuredId &&
    !showAddMcpPanel &&
    !showViewMcpPanel &&
    !showPolicyDetail &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill &&
    !showCommissionScan;
  const showRunDetail =
    !!runDetailPanel &&
    !showAddMcpPanel &&
    !showViewMcpPanel &&
    !showCommissionScan &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill &&
    !showPolicyDetail &&
    !showInsuredDetail;
  const showCallDetail =
    !!callDetailPanel &&
    !showAddMcpPanel &&
    !showViewMcpPanel &&
    !showCommissionScan &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill &&
    !showPolicyDetail &&
    !showInsuredDetail &&
    !showRunDetail;
  const showDialerCallDetail =
    !!dialerCallDetail &&
    !showAddMcpPanel &&
    !showViewMcpPanel &&
    !showCommissionScan &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill &&
    !showPolicyDetail &&
    !showInsuredDetail &&
    !showRunDetail &&
    !showCallDetail;
  const showCarrierSession =
    !!sessionPanel &&
    !showAddMcpPanel &&
    !showViewMcpPanel &&
    !showCommissionScan &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill &&
    !showPolicyDetail &&
    !showInsuredDetail &&
    !showRunDetail &&
    !showCallDetail &&
    !showDialerCallDetail;

  const completedCount =
    statusTemplate?.statusSteps.filter((s) => s.status === 'completed')
      .length ?? 0;
  const totalSteps = statusTemplate?.statusSteps.length ?? 0;
  const progressPct = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;
  const showStatusTemplate =
    !!statusTemplate &&
    !showAddMcpPanel &&
    !showViewMcpPanel &&
    !showMarketplace &&
    !showCreateApiKey &&
    !showAddChannel &&
    !showOpenClawPanel &&
    !showAddClawSkill &&
    !showInsuredDetail &&
    !showPolicyDetail;

  const [clawSkillName, setClawSkillName] = useState('');
  const [clawSkillContent, setClawSkillContent] = useState('');
  const [clawSkillCreating, setClawSkillCreating] = useState(false);
  const [clawSkillError, setClawSkillError] = useState<string | null>(null);

  const [mcpName, setMcpName] = useState('');
  const [mcpDescription, setMcpDescription] = useState('');
  const [mcpUrl, setMcpUrl] = useState('');
  // Single-credential model: Managed Agents' vault only ever recognizes a header literally
  // named "Authorization" (static_bearer, matched by URL) — anything else is silently inert
  // for that path, though it's still genuinely used verbatim by the legacy LangChain-based
  // chat routes' MultiServerMCPClient. So: one slot, a name mode instead of free-text-by-default,
  // "Custom" for the cases that actually need a different header name.
  const [credNameMode, setCredNameMode] = useState<'authorization' | 'custom'>('authorization');
  const [credCustomName, setCredCustomName] = useState('');
  const [credValue, setCredValue] = useState('');
  const [credHasExisting, setCredHasExisting] = useState(false);
  // Whether the value input is showing (vs. the masked "value already set" display + Update button).
  const [credEditing, setCredEditing] = useState(true);
  const [mcpTrust, setMcpTrust] = useState(false);
  const [mcpSubmitted, setMcpSubmitted] = useState(false);
  const [mcpSaving, setMcpSaving] = useState(false);
  const [showToolsList, setShowToolsList] = useState(false);

  const mcpEditData =
    feedbackPanel?.type === 'add-mcp' ? feedbackPanel.editData : undefined;
  const mcpViewData =
    feedbackPanel?.type === 'view-mcp' ? feedbackPanel : undefined;

  useLayoutEffect(() => {
    if (
      feedbackPanel?.type === 'add-mcp' ||
      feedbackPanel?.type === 'view-mcp'
    ) {
      const edit =
        feedbackPanel.type === 'add-mcp'
          ? feedbackPanel.editData
          : feedbackPanel;
      setMcpName(edit?.name ?? '');
      setMcpDescription(edit?.description ?? '');
      setMcpUrl(edit?.url ?? '');

      // A row carries at most one meaningful header entry under this model. Figure out its
      // name (if any) so the dropdown/name field reflect what's actually configured.
      let existingName: string | null = null;
      if (edit?.headers) {
        try {
          const raw = JSON.parse(edit.headers);
          if (Array.isArray(raw) && raw.length > 0 && raw[0] && typeof raw[0] === 'object') {
            const keys = Object.keys(raw[0]);
            if (keys.length > 0) existingName = keys[0];
          }
        } catch {
          /* ignore */
        }
      }

      if (existingName && existingName.toLowerCase() === 'authorization') {
        // The list endpoint masks Authorization's value down to an empty string (itself the
        // "preserve existing" sentinel on submit) — that emptiness can't tell us whether a
        // real key is set, so needsUserCredentials is the actual signal for that.
        const stillNeedsCredential = edit?.needsUserCredentials ?? false;
        setCredNameMode('authorization');
        setCredCustomName('');
        setCredHasExisting(!stillNeedsCredential);
        setCredEditing(stillNeedsCredential);
      } else if (existingName) {
        setCredNameMode('custom');
        setCredCustomName(existingName);
        setCredHasExisting(true);
        setCredEditing(false);
      } else {
        setCredNameMode('authorization');
        setCredCustomName('');
        setCredHasExisting(false);
        setCredEditing(true);
      }
      setCredValue('');
      setMcpTrust(false);
      setMcpSubmitted(false);
      setShowToolsList(false);
    }
  }, [feedbackPanel]);
  const [mcpToast, setMcpToast] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!showAddClawSkill) {
      setClawSkillName('');
      setClawSkillContent('');
      setClawSkillCreating(false);
      setClawSkillError(null);
    } else if (editClawSkillData) {
      setClawSkillName(editClawSkillData.name);
      setClawSkillContent(editClawSkillData.skill);
      setClawSkillError(null);
    }
  }, [showAddClawSkill, editClawSkillData]);

  const handleClawSkillSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (
        !clawSkillName.trim() ||
        !clawSkillContent.trim() ||
        clawSkillCreating
      )
        return;
      setClawSkillCreating(true);
      setClawSkillError(null);
      const { createClawSkill } =
        await import('../services/momentumClawService');
      const ok = await createClawSkill(
        clawSkillName.trim(),
        clawSkillContent.trim(),
      );
      setClawSkillCreating(false);
      if (ok) {
        onClawSkillCreated?.();
        onCloseAddClawSkill?.();
      } else {
        setClawSkillError('Failed to create skill. Please try again.');
      }
    },
    [
      clawSkillName,
      clawSkillContent,
      clawSkillCreating,
      onClawSkillCreated,
      onCloseAddClawSkill,
    ],
  );

  const [resizeWidth, setResizeWidth] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const asideRef = useRef<HTMLElement>(null);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current =
        asideRef.current?.offsetWidth ?? resizeWidth ?? 360;
      setIsDragging(true);

      const onMouseMove = (ev: MouseEvent) => {
        const delta = startXRef.current - ev.clientX;
        const newWidth = Math.min(
          Math.max(startWidthRef.current + delta, 280),
          window.innerWidth * 0.6,
        );
        setResizeWidth(newWidth);
      };

      const onMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    },
    [resizeWidth],
  );

  // Collapse the main (center) panel from the sidebar header, next to the
  // sidebar's own collapse button. Lives here rather than floating over the
  // center panel, where it collided with page headers' top-right actions.
  const mainPanelCollapseBtn =
    onToggleMainPanel && !mainCollapsed ? (
      <button
        className='sidebar-right__action-btn'
        title='Collapse main panel'
        onClick={onToggleMainPanel}
      >
        <FiChevronLeft size={15} />
      </button>
    ) : null;

  return (
    <aside
      ref={asideRef}
      className={`sidebar-right ${isOpen ? 'sidebar-right--open' : 'sidebar-right--closed'}`}
      style={
        isOpen && resizeWidth
          ? { width: resizeWidth, transition: isDragging ? 'none' : undefined }
          : isOpen && showRateQuoteLink
            ? { width: 500, transition: 'width 0.25s ease' }
            : isOpen && showToolLog
              ? { width: 360, transition: 'width 0.25s ease' }
              : undefined
      }
    >
      {/* Resize handle — visible only when open */}
      {isOpen && (
        <div
          onMouseDown={handleResizeStart}
          className="sidebar-right__resize-handle"
          style={{
            background: isDragging ? 'rgba(139,92,246,0.5)' : 'transparent',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.background =
              'rgba(139,92,246,0.35)';
          }}
          onMouseLeave={(e) => {
            if (!isDragging)
              (e.currentTarget as HTMLDivElement).style.background =
                'transparent';
          }}
        />
      )}

      {/* Collapsed strip — always in DOM, visible only when closed */}
      <div className='sidebar-right__strip'>
        {/* Expand Right Sidebar Button */}
        <button
          className='sidebar-right__strip-btn sidebar-right__strip-btn--toggle'
          onClick={onToggle}
          title={
            showRateQuoteLink ? 'Expand Personal Rating' : 'Expand Analytics'
          }
        >
          <FiChevronLeft size={16} />
        </button>

        {/* Focus Mode Button - Collapse Main + Expand Right */}
        {onToggleMainPanel && !mainCollapsed && (
          <button
            className='sidebar-right__strip-btn sidebar-right__strip-btn--focus'
            onClick={() => {
              onToggleMainPanel(); // Collapse main panel
              if (!isOpen) {
                onToggle(); // Expand right sidebar if not already open
              }
            }}
            title='Focus Mode (Collapse main panel)'
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <rect x='3' y='3' width='7' height='7'></rect>
              <rect x='14' y='3' width='7' height='7'></rect>
              <rect x='14' y='14' width='7' height='7'></rect>
              <rect x='3' y='14' width='7' height='7'></rect>
            </svg>
          </button>
        )}

        <button className='sidebar-right__strip-btn' title='Clients by Agent'>
          <FiBarChart2 size={16} />
        </button>
        <button className='sidebar-right__strip-btn' title='Policy Breakdown'>
          <FiBarChart2 size={16} />
        </button>
        <button className='sidebar-right__strip-btn' title='Policy Table'>
          <FiBarChart2 size={16} />
        </button>
      </div>

      {/* Expanded panel — always in DOM, visible only when open */}
      <div className='sidebar-right__panel'>
        <div className='sidebar-right__header'>
          {(() => {
            /* Determine contextual header based on active panel */
            const panelTitle = showChannelDocPanel
              ? 'Documents'
              : showAddMcpPanel
                ? mcpEditData?.transactionId
                  ? 'Edit MCP Server'
                  : 'New Custom MCP Server'
                : showViewMcpPanel
                  ? 'View MCP Server'
                  : showCertificates
                    ? 'Sent Certificates'
                    : showRateQuoteLink
                      ? 'Personal Rating'
                      : showCommissionScan
                        ? 'Scan Details'
                        : showDocumentPanel
                          ? isTruckingPage
                            ? 'Trucking Documents'
                            : 'Carrier Finder Documents'
                          : showMarketplace && marketplaceModule
                            ? marketplaceModule.name
                            : showAddChannel
                              ? editingChannel
                                ? 'Edit Workspace'
                                : 'Create New Workspace'
                              : showToolLog
                                ? 'Tool Call Log'
                                : showCreateAgent
                                  ? 'New Agent'
                                  : showCreateApiKey
                                    ? 'Create API Key'
                                    : null;

            const panelClose = showChannelDocPanel
              ? closeChannelDocPanel
              : showAddMcpPanel
                ? () => setFeedbackPanel(null)
                : showViewMcpPanel
                  ? () => setFeedbackPanel(null)
                  : showCommissionScan
                    ? onCloseCommissionScan
                    : showDocumentPanel
                      ? () => setShowDocPanel(false)
                      : showMarketplace
                        ? onCloseMarketplaceDetail
                        : showAddChannel
                          ? onCloseAddChannel
                          : showCreateAgent
                            ? onCloseCreateAgent
                            : showCreateApiKey
                              ? onCloseCreateApiKey
                              : null;

            if (panelTitle) {
              return (
                <>
                  <div className="sidebar-header__title-row--wide">
                    {showChannelDocPanel && (
                      <div className="sidebar-header__icon-badge sidebar-header__icon-badge--cyan">
                        <FiFileText size={13} />
                      </div>
                    )}
                    {showAddMcpPanel && (
                      <div className="sidebar-header__icon-badge sidebar-header__icon-badge--purple">
                        <FiSettings size={13} />
                      </div>
                    )}
                    {showViewMcpPanel && (
                      <div className="sidebar-header__icon-badge sidebar-header__icon-badge--cyan">
                        <FiEye size={13} />
                      </div>
                    )}
                    {showRateQuoteLink && (
                      <div className="sidebar-header__icon-badge sidebar-header__icon-badge--green">
                        <FiZap size={13} />
                      </div>
                    )}
                    {showMarketplace && marketplaceModule && (
                      <div
                        className="sidebar-header__icon-badge sidebar-header__icon-badge--lg"
                        style={{
                          background: marketplaceModule.iconColor + '25',
                          color: marketplaceModule.iconColor,
                        }}
                      >
                        <FiZap size={14} />
                      </div>
                    )}
                    <span className="sidebar-header__title">
                      {panelTitle}
                    </span>
                  </div>
                  <div className="sidebar-right__actions">
                    {panelClose && (
                      <button
                        className='sidebar-right__action-btn'
                        title='Close'
                        onClick={panelClose}
                      >
                        <FiX size={15} />
                      </button>
                    )}
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Run Details Panel */
            if (showRunDetail) {
              const sCfg = {
                done: {
                  label: 'Done',
                  color: '#34d399',
                  bg: 'rgba(16,185,129,0.12)',
                  border: 'rgba(16,185,129,0.3)',
                  Icon: FiCheck,
                },
                failed: {
                  label: 'Failed',
                  color: '#f87171',
                  bg: 'rgba(239,68,68,0.1)',
                  border: 'rgba(239,68,68,0.28)',
                  Icon: FiAlertTriangle,
                },
                running: {
                  label: 'Running',
                  color: '#fbbf24',
                  bg: 'rgba(245,158,11,0.12)',
                  border: 'rgba(245,158,11,0.3)',
                  Icon: FiRefreshCw,
                },
              } as const;
              const runCfg = sCfg[runDetailPanel!.run.status] ?? sCfg.done;
              const runIsDone = runDetailPanel!.run.status === 'done';
              return (
                <>
                  <div className="sidebar-header__title-row--wide">
                    <div className="sidebar-header__icon-badge sidebar-header__icon-badge--violet">
                      <FiRefreshCw size={13} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 1 }}>
                      <span className="sidebar-header__title sidebar-header__title--ls">
                        {RUN_DETAIL_TITLE[runDetailPanel!.kind ?? 'chat']}
                      </span>
                      <span style={{ fontSize: 10.5, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {fmtRunDetailDate(runDetailPanel!.run.startedAt)}
                      </span>
                    </div>
                    {!runIsDone && (
                      <span
                        className="sidebar-header__status-pill sidebar-header__status-pill--gap"
                        style={{
                          background: runCfg.bg,
                          border: `1px solid ${runCfg.border}`,
                          color: runCfg.color,
                        }}
                      >
                        <runCfg.Icon size={9} /> {runCfg.label}
                      </span>
                    )}
                  </div>
                  <div className='sidebar-right__actions'>
                    <button
                      className='sidebar-right__action-btn'
                      title='Close'
                      onClick={() => setRunDetailPanel(null)}
                    >
                      <FiX size={15} />
                    </button>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Call Detail Panel */
            if (showCallDetail) {
              return (
                <>
                  <div className="sidebar-header__title-row--wide">
                    <div className="sidebar-header__icon-badge sidebar-header__icon-badge--violet">
                      <FiPhone size={13} />
                    </div>
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      Call Detail
                    </span>
                  </div>
                  <div className="sidebar-right__actions">
                    <button
                      className='sidebar-right__action-btn'
                      title='Close'
                      onClick={() => setCallDetailPanel(null)}
                    >
                      <FiX size={15} />
                    </button>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Dialer Call Log Detail Panel */
            if (showDialerCallDetail) {
              return (
                <>
                  <div className="sidebar-header__title-row--wide">
                    <div className="sidebar-header__icon-badge sidebar-header__icon-badge--violet">
                      <FiPhone size={13} />
                    </div>
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      Call Details
                    </span>
                  </div>
                  <div className="sidebar-right__actions">
                    <button
                      className='sidebar-right__action-btn'
                      title='Close'
                      onClick={() => setDialerCallDetail(null)}
                    >
                      <FiX size={15} />
                    </button>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Policy Detail Panel */
            if (showPolicyDetail) {
              return (
                <>
                  <div className="sidebar-header__title-row">
                    <FiFileText
                      size={13}
                      className="sidebar-header__icon sidebar-header__icon--blue"
                    />
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      Policy Details
                    </span>
                  </div>
                  <div className="sidebar-right__actions">
                    <button
                      className='sidebar-right__action-btn'
                      title='Close'
                      onClick={() => setSelectedPolicyId(null)}
                    >
                      <FiX size={15} />
                    </button>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Insured Detail Panel */
            if (showInsuredDetail) {
              return (
                <>
                  <div className="sidebar-header__title-row">
                    <FiSearch
                      size={13}
                      className="sidebar-header__icon sidebar-header__icon--cyan"
                    />
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      Insured Details
                    </span>
                  </div>
                  <div className="sidebar-right__actions">
                    <button
                      className='sidebar-right__action-btn'
                      title='Close'
                      onClick={() => {
                        setSelectedInsuredId(null);
                        onCloseInsuredDetail?.();
                      }}
                    >
                      <FiX size={15} />
                    </button>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Carrier Session Detail Panel */
            if (showCarrierSession) {
              const row = sessionPanel!.row;
              const consumedCfg =
                row.consumed === 'Consumed'
                  ? {
                      label: 'Consumed',
                      color: '#4ED56E',
                      bg: 'rgba(78,213,110,0.12)',
                      border: 'rgba(78,213,110,0.3)',
                    }
                  : row.consumed === 'Partially_Consumed'
                    ? {
                        label: 'Partial',
                        color: '#FEAD0F',
                        bg: 'rgba(254,173,15,0.12)',
                        border: 'rgba(254,173,15,0.3)',
                      }
                    : {
                        label: row.consumed.replace(/_/g, ' '),
                        color: '#f87171',
                        bg: 'rgba(239,68,68,0.1)',
                        border: 'rgba(239,68,68,0.28)',
                      };
              return (
                <>
                  <div className="sidebar-header__title-row--wide">
                    <div className="sidebar-header__icon-badge sidebar-header__icon-badge--brand-purple">
                      <FiList size={13} />
                    </div>
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      Session Details
                    </span>
                    <span
                      className="sidebar-header__status-pill"
                      style={{
                        background: consumedCfg.bg,
                        border: `1px solid ${consumedCfg.border}`,
                        color: consumedCfg.color,
                      }}
                    >
                      {consumedCfg.label}
                    </span>
                  </div>
                  <div className='sidebar-right__actions'>
                    <button
                      className='sidebar-right__action-btn'
                      title='Close'
                      onClick={closeSession}
                    >
                      <FiX size={15} />
                    </button>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Status Template — Renewal / Lead */
            if (showStatusTemplate) {
              return (
                <>
                  <div className="sidebar-header__title-row">
                    <RiSparklingFill
                      size={13}
                      className="sidebar-header__icon sidebar-header__icon--purple"
                    />
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      Status
                    </span>
                  </div>
                  <div className="sidebar-header__status-actions">
                    <span className='rsp__counter'>
                      {completedCount}/{totalSteps}
                    </span>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Add Claw Skill Panel */
            if (showAddClawSkill) {
              return (
                <>
                  <div className="sidebar-header__title-row">
                    <span className="sidebar-header__emoji">🦀</span>
                    <span className="sidebar-header__title--notrunc">
                      {editClawSkillData ? 'Edit Skill' : 'Add New Skill'}
                    </span>
                  </div>
                  <div className='sidebar-right__actions'>
                    <button
                      className='sidebar-right__action-btn'
                      title='Close'
                      onClick={onCloseAddClawSkill}
                    >
                      <FiX size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* OpenClaw Panel — VM Desktop View */
            if (showOpenClawPanel) {
              return (
                <>
                  <div className="sidebar-header__title-row">
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      MomentumClaw
                    </span>
                    <span className="sidebar-header__settings-chip">
                      <FiSettings size={11} className="sidebar-header__settings-chip-icon" />
                    </span>
                  </div>
                  <div className='sidebar-right__actions'>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Kanban Configuration Panel */
            if (showKanbanConfig) {
              return (
                <>
                  <div className="sidebar-header__title-row">
                    <FiList
                      size={13}
                      className="sidebar-header__icon sidebar-header__icon--purple"
                    />
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      Command Center
                    </span>
                  </div>
                  <div className='sidebar-right__actions'>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* MAC Workflows page */
            if (isMacWorkflowsPage) {
              return (
                <>
                  <div className="sidebar-header__title-row">
                    <FiZap
                      size={13}
                      className="sidebar-header__icon sidebar-header__icon--brand"
                    />
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      MAC Workflows
                    </span>
                  </div>
                  <div className='sidebar-right__actions'>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Quote Details — a submission picked in Submission Center */
            if (showSubmissionQuoteDetail) {
              return (
                <>
                  <div className="sidebar-header__title-row">
                    <FiFileText
                      size={13}
                      className="sidebar-header__icon sidebar-header__icon--blue"
                    />
                    <span className="sidebar-header__title sidebar-header__title--ls">
                      Quote Details
                    </span>
                  </div>
                  <div className="sidebar-right__actions">
                    <button
                      className='sidebar-right__action-btn'
                      title='Close'
                      onClick={() => setSubmissionSelected(null)}
                    >
                      <FiX size={15} />
                    </button>
                    {mainPanelCollapseBtn}
                    <button
                      className='sidebar-right__action-btn'
                      title='Collapse sidebar'
                      onClick={onToggle}
                    >
                      <FiChevronRight size={15} />
                    </button>
                  </div>
                </>
              );
            }

            /* Default — Reports Dashboard */
            return (
              <>
                <div className="sidebar-header__title-row">
                  <span className="sidebar-header__title sidebar-header__title--ls">
                    {isDwArtifactsPage ? 'Digital Workforce' : isArtifactsPage ? 'Artifacts' : isRoutinesPage ? 'Momentum Routines' : isCarrierDownloadPage ? 'Carrier Download' : isVoicePage ? 'Momentum Voice' : isDialerPage ? 'Dialer' : isCommunicationCenterPage ? 'Communication Center' : isSubmissionCenterPage ? 'Submission Center' : isWebhooksPage ? 'Webhook Analytics' : 'Reports Dashboard'}
                  </span>
                  {/* <span style={{
                    display: 'flex', alignItems: 'center',
                    background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.35)',
                    borderRadius: 4, padding: '2px 5px', flexShrink: 0,
                  }}>
                    <FiSettings size={11} style={{ color: 'white' }} />
                  </span> */}
                </div>
                <div className='sidebar-right__actions'>
                  {mainPanelCollapseBtn}
                  <button
                    className='sidebar-right__action-btn'
                    title='Collapse sidebar'
                    onClick={onToggle}
                  >
                    <FiChevronRight size={15} />
                  </button>
                </div>
              </>
            );
          })()}
        </div>

        <div
          className={`sidebar-right__content${showAddChannel ? ' sidebar-right__content--add-channel' : ''}${showMarketplace ? ' sidebar-right__content--marketplace' : ''}${showCreateApiKey ? ' sidebar-right__content--create-apikey' : ''}${showCommissionScan ? ' sidebar-right__content--commission-scan' : ''}${showOpenClawPanel ? ' sidebar-right__content--openclaw' : ''}${showInsuredDetail ? ' sidebar-right__content--insured-detail' : ''}${showRunDetail ? ' sidebar-right__content--run-detail' : ''}`}
        >
          {showChannelDocPanel ? (
            <ChannelDocumentsSidebarPanel
              channelId={channelDocChannelId}
              onClose={closeChannelDocPanel}
            />
          ) : showCommissionScan ? (
            <CommissionScanSidebarPanel
              pdf={commissionScanData!.pdf}
              table={commissionScanData!.table}
              conversationId={commissionScanData!.conversationId}
            />
          ) : showMarketplace ? (
            <MarketplaceSidebarPanel
              module={marketplaceModule!}
              onInstall={onMarketplaceInstall}
              onUninstall={onMarketplaceUninstall}
              isProcessing={marketplaceIsProcessing}
            />
          ) : showCreateApiKey ? (
            <CreateApiKeySidebarPanel
              onClose={onCloseCreateApiKey ?? (() => {})}
              onKeyCreated={onApiKeyCreated ?? (() => {})}
            />
          ) : showToolLog ? (
            <SidebarToolLogPanel
              entries={toolLogEntries as ToolLogEntry[]}
              onClose={onCloseToolLog ?? (() => {})}
            />
          ) : showCreateAgent ? (
            <CreateAgentPanel onClose={onCloseCreateAgent ?? (() => {})} />
          ) : showAddChannel ? (
            <AddChannelModal
              isOpen={showAddChannel}
              onClose={onCloseAddChannel ?? (() => {})}
              onAdd={onAddChannelSubmit ?? (() => {})}
              onSave={onEditChannelSubmit}
              channelToEdit={editingChannel}
              inline
            />
          ) : showAddMcpPanel || showViewMcpPanel ? (
            (() => {
              const isMcpViewOnly = showViewMcpPanel;
              const mcpIdentity =
                mcpEditData?.transactionId ?? mcpViewData?.transactionId;
              const viewOnlyFieldStyle = isMcpViewOnly
                ? ({
                    opacity: 0.75,
                    cursor: 'default',
                    background: 'rgba(255,255,255,0.03)',
                  } as const)
                : {};
              return (
                <div className="mcp-panel__root">
                  <div className="mcp-panel__body">
                    <p className="mcp-panel__intro">
                      {isMcpViewOnly
                        ? 'Read-only details for this MCP server integration.'
                        : 'Connect a custom Model Context Protocol (MCP) server to add powerful capabilities to your agent.'}
                    </p>

                    {/* Basic Information */}
                    <div className='mcp-panel__section'>
                      <div className="mcp-panel__section-title">
                        Basic Information
                      </div>
                      <div className="mcp-panel__section-subtitle">
                        Identify your MCP server with a clear name and
                        description.
                      </div>
                      <label className="mcp-panel__label">
                        Name{' '}
                        {!isMcpViewOnly && (
                          <span className="mcp-panel__required">*</span>
                        )}
                      </label>
                      <input
                        value={mcpName}
                        onChange={(e) => setMcpName(e.target.value)}
                        readOnly={isMcpViewOnly}
                        className='mcp-panel__field mcp-panel__input-base'
                        style={{
                          marginBottom:
                            mcpSubmitted && !mcpName.trim() ? 4 : 10,
                          ...viewOnlyFieldStyle,
                          ...(mcpSubmitted && !mcpName.trim()
                            ? { borderColor: 'rgba(239,68,68,0.6)' }
                            : {}),
                        }}
                      />
                      {mcpSubmitted && !mcpName.trim() && (
                        <div className="mcp-panel__field-error--below">
                          Name is required.
                        </div>
                      )}
                      <label className="mcp-panel__label">
                        Description{' '}
                        {!isMcpViewOnly && (
                          <span className="mcp-panel__required">*</span>
                        )}
                      </label>
                      <textarea
                        value={mcpDescription}
                        onChange={(e) => setMcpDescription(e.target.value)}
                        rows={3}
                        readOnly={isMcpViewOnly}
                        className='mcp-panel__field mcp-panel__textarea-base'
                        style={{
                          marginBottom:
                            mcpSubmitted && !mcpDescription.trim() ? 4 : 0,
                          ...viewOnlyFieldStyle,
                          ...(mcpSubmitted && !mcpDescription.trim()
                            ? { borderColor: 'rgba(239,68,68,0.6)' }
                            : {}),
                        }}
                      />
                      {mcpSubmitted && !mcpDescription.trim() && (
                        <div className="mcp-panel__field-error">
                          Description is required.
                        </div>
                      )}
                    </div>

                    {/* Server Configuration */}
                    <div className='mcp-panel__section'>
                      <div className="mcp-panel__section-title">
                        Server Configuration
                      </div>
                      <div className="mcp-panel__section-subtitle">
                        Specify how to connect to your MCP server.
                      </div>
                      <label className="mcp-panel__label mcp-panel__label--mb6">
                        Server URL{' '}
                        {!isMcpViewOnly && (
                          <span className="mcp-panel__required">*</span>
                        )}
                      </label>
                      <input
                        value={mcpUrl}
                        onChange={(e) => setMcpUrl(e.target.value)}
                        placeholder='https://example.com/mcp'
                        readOnly={isMcpViewOnly}
                        className='mcp-panel__field mcp-panel__input-base mcp-panel__input-base--sm'
                        style={{
                          ...viewOnlyFieldStyle,
                          ...(mcpSubmitted && !mcpUrl.trim()
                            ? { borderColor: 'rgba(239,68,68,0.6)' }
                            : {}),
                        }}
                      />
                      {mcpSubmitted && !mcpUrl.trim() && (
                        <div className="mcp-panel__field-error">
                          Server URL is required.
                        </div>
                      )}
                    </div>

                    {/* Credential — Managed Agents' vault only ever recognizes a header
                        literally named "Authorization" (matched to the server by URL); any
                        other name is real and does get sent by the legacy chat routes, but is
                        silently unused by Digital Workforce agents. One slot, name mode instead
                        of free-text-by-default, "Custom" for the cases that actually need it. */}
                    {(() => {
                      const isMomentumUrl =
                        mcpUrl.includes('mcp-stage.momentumamp.com') ||
                        mcpUrl.includes('mcp.momentumamp.com');
                      const lockedStyle = {
                        opacity: 0.6,
                        cursor: 'default',
                        background: 'rgba(255,255,255,0.03)',
                      } as const;
                      const effectiveName =
                        credNameMode === 'custom' ? credCustomName.trim() : 'Authorization';
                      const nameMissing =
                        credNameMode === 'custom' &&
                        !credCustomName.trim() &&
                        (credEditing ? credValue.trim() !== '' : false);
                      return (
                        <div className='mcp-panel__section'>
                          <div className="mcp-panel__section-title--flush">
                            Credential
                          </div>
                          <div
                            className="mcp-panel__hint-text"
                            style={{ marginBottom: 10 }}
                          >
                            {isMcpViewOnly
                              ? 'Authentication configured for this server.'
                              : 'Most MCP servers authenticate via a Bearer token sent in the Authorization header. Choose "Custom" only if this one needs something else.'}
                          </div>

                          <div className='mcp-panel__header-item'>
                            <label className="mcp-panel__mini-label">
                              Header name
                            </label>
                            {isMcpViewOnly ? (
                              <input
                                value={effectiveName || '—'}
                                readOnly
                                className='mcp-panel__field mcp-panel__field--sm mcp-panel__input-base'
                                style={{ marginBottom: 8, ...lockedStyle }}
                              />
                            ) : (
                              <select
                                value={credNameMode}
                                onChange={(e) => {
                                  setCredNameMode(e.target.value as 'authorization' | 'custom');
                                }}
                                className='mcp-panel__field mcp-panel__field--sm mcp-panel__input-base'
                                style={{ marginBottom: 8 }}
                              >
                                <option value="authorization">Authorization (recommended)</option>
                                <option value="custom">Custom header name</option>
                              </select>
                            )}

                            {credNameMode === 'custom' && (
                              <>
                                {!isMcpViewOnly && (
                                  <input
                                    value={credCustomName}
                                    onChange={(e) => setCredCustomName(e.target.value)}
                                    placeholder='e.g. X-API-Key'
                                    className='mcp-panel__field mcp-panel__field--sm mcp-panel__input-base'
                                    style={{
                                      marginBottom: nameMissing ? 4 : 8,
                                      ...(nameMissing
                                        ? { borderColor: 'rgba(239,68,68,0.6)' }
                                        : {}),
                                    }}
                                  />
                                )}
                                {nameMissing && (
                                  <div className="mcp-panel__header-field-error">
                                    Header name is required.
                                  </div>
                                )}
                              </>
                            )}

                            <label className="mcp-panel__mini-label">
                              Value
                            </label>
                            {credHasExisting && !credEditing ? (
                              <>
                                <input
                                  value='••••••••••••••••••••••••••••••••'
                                  readOnly
                                  type='text'
                                  className='mcp-panel__field mcp-panel__field--sm mcp-panel__masked-value'
                                  style={lockedStyle}
                                />
                                {!isMcpViewOnly && (
                                  <>
                                    <div className="mcp-panel__auth-hint">
                                      Value already set.
                                    </div>
                                    <button
                                      type="button"
                                      className='mcp-panel__add-btn'
                                      onClick={() => setCredEditing(true)}
                                    >
                                      Update
                                    </button>
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                <input
                                  value={credValue}
                                  readOnly={isMcpViewOnly}
                                  onChange={(e) => setCredValue(e.target.value)}
                                  onBlur={() => {
                                    if (
                                      isMomentumUrl &&
                                      effectiveName.toLowerCase() === 'authorization' &&
                                      credValue.trim() &&
                                      !credValue.startsWith('Bearer ')
                                    ) {
                                      setCredValue(`Bearer ${credValue.trim()}`);
                                    }
                                  }}
                                  type='password'
                                  placeholder={credHasExisting ? 'Enter a new value to replace it' : 'Enter the API key / token'}
                                  className='mcp-panel__field mcp-panel__field--sm mcp-panel__input-base'
                                />
                                {credHasExisting && !isMcpViewOnly && (
                                  <button
                                    type="button"
                                    className='mcp-panel__add-btn'
                                    style={{ marginTop: 6 }}
                                    onClick={() => {
                                      setCredEditing(false);
                                      setCredValue('');
                                    }}
                                  >
                                    Cancel
                                  </button>
                                )}
                                {isMomentumUrl &&
                                  effectiveName.toLowerCase() === 'authorization' &&
                                  !credValue.startsWith('Bearer ') &&
                                  credValue.trim() && (
                                    <div className="mcp-panel__bearer-hint">
                                      "Bearer " will be prepended automatically on save
                                    </div>
                                  )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Tools — edit/view mode only, when tools data is available */}
                    {(() => {
                      if (!mcpIdentity) return null;
                      let tools: { Name: string }[] = [];
                      try {
                        const toolsJson =
                          mcpEditData?.tools ?? mcpViewData?.tools;
                        if (toolsJson) {
                          const parsed = JSON.parse(toolsJson);
                          if (Array.isArray(parsed)) tools = parsed;
                        }
                      } catch {
                        /* ignore */
                      }
                      if (tools.length === 0) return null;
                      return (
                        <div className='mcp-panel__section'>
                          <div
                            className="mcp-panel__row-between"
                            style={{ marginBottom: showToolsList ? 10 : 0 }}
                          >
                            <div className="mcp-panel__section-title--flush">
                              Tools
                            </div>
                            <button
                              className='mcp-panel__tools-btn'
                              onClick={() => setShowToolsList((v) => !v)}
                            >
                              <FiZap size={10} />
                              {tools.length} tool{tools.length !== 1 ? 's' : ''}
                              <FiChevronDown
                                size={10}
                                className="mcp-panel__tools-chevron"
                                style={{
                                  transform: showToolsList
                                    ? 'rotate(180deg)'
                                    : 'none',
                                }}
                              />
                            </button>
                          </div>
                          {showToolsList && (
                            <div className="mcp-panel__tools-list">
                              {tools.map((tool, i) => (
                                <div key={i} className='mcp-panel__tool-item'>
                                  <FiZap
                                    size={10}
                                    className="mcp-panel__tool-icon"
                                  />
                                  <span className="mcp-panel__tool-name">
                                    {tool.Name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {/* end scrollable content */}

                  {/* Footer */}
                  <div className='mcp-panel__footer'>
                    {isMcpViewOnly ? (
                      <button
                        onClick={() => setFeedbackPanel(null)}
                        className="mcp-panel__close-btn"
                      >
                        Close
                      </button>
                    ) : (
                      <>
                        <label className="mcp-panel__trust-label">
                          <input
                            type='checkbox'
                            checked={mcpTrust}
                            onChange={(e) => setMcpTrust(e.target.checked)}
                            className="mcp-panel__trust-checkbox"
                          />
                          <span className="mcp-panel__trust-text">
                            I trust this server
                          </span>
                        </label>
                        <p className="mcp-panel__disclaimer">
                          Custom MCP servers are not verified by Momentum AMP.
                        </p>
                        <div className="mcp-panel__btn-row">
                          <button
                            className='mcp-panel__cancel'
                            onClick={() => setFeedbackPanel(null)}
                            disabled={mcpSaving}
                            style={{
                              opacity: mcpSaving ? 0.5 : 1,
                              cursor: mcpSaving ? 'not-allowed' : 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={async () => {
                              setMcpSubmitted(true);
                              const isEdit = !!mcpEditData?.transactionId;
                              const effectiveName =
                                credNameMode === 'custom' ? credCustomName.trim() : 'Authorization';
                              const enteringNewValue = credEditing && credValue.trim() !== '';
                              const nameValid =
                                credNameMode !== 'custom' || !enteringNewValue || !!effectiveName;
                              if (
                                !mcpName.trim() ||
                                !mcpDescription.trim() ||
                                !mcpUrl.trim() ||
                                !nameValid
                              )
                                return;
                              setMcpSaving(true);
                              try {
                                const { saveExternalMcp, editExternalMcp } =
                                  await import('../services/marketplaceService');
                                const isMomentumUrl =
                                  mcpUrl.includes('mcp-stage.momentumamp.com') ||
                                  mcpUrl.includes('mcp.momentumamp.com');
                                // One credential slot total. A new value replaces it outright;
                                // no new value means "leave whatever's there alone" — sending
                                // headers: null lets the backend's COALESCE preserve the
                                // existing name+value untouched (works regardless of what the
                                // existing header happens to be named, unlike the old
                                // Authorization-only empty-string sentinel).
                                let headers: Array<Record<string, string>> | null;
                                let vaultToken: string | null = null;
                                if (enteringNewValue) {
                                  let value = credValue.trim();
                                  if (
                                    isMomentumUrl &&
                                    effectiveName.toLowerCase() === 'authorization' &&
                                    !value.startsWith('Bearer ')
                                  ) {
                                    value = `Bearer ${value}`;
                                  }
                                  headers = effectiveName ? [{ [effectiveName]: value }] : [];
                                  // Anthropic's Managed Agents vault only understands a plain
                                  // Authorization bearer token (static_bearer) — a Custom header
                                  // name can't be represented there regardless, so only sync when
                                  // the credential is actually named Authorization.
                                  if (effectiveName.toLowerCase() === 'authorization') {
                                    vaultToken = value.replace(/^Bearer\s+/i, '');
                                  }
                                } else if (isEdit) {
                                  headers = null;
                                } else {
                                  headers = [];
                                }
                                if (mcpEditData?.transactionId) {
                                  const result = await editExternalMcp({
                                    transactionId: mcpEditData.transactionId,
                                    name: mcpName.trim(),
                                    description: mcpDescription.trim(),
                                    url: mcpUrl.trim(),
                                    headers,
                                  });
                                  if ('success' in result && !result.success) {
                                    setMcpToast({
                                      type: 'error',
                                      message:
                                        result.errorMessage ||
                                        'Update failed. Please try again.',
                                    });
                                    setTimeout(() => setMcpToast(null), 4000);
                                    return;
                                  }
                                } else {
                                  const result = await saveExternalMcp({
                                    name: mcpName.trim(),
                                    description: mcpDescription.trim(),
                                    url: mcpUrl.trim(),
                                    headers,
                                  });
                                  if ('success' in result && !result.success) {
                                    setMcpToast({
                                      type: 'error',
                                      message:
                                        result.errorMessage ||
                                        'Failed to add server. Please try again.',
                                    });
                                    setTimeout(() => setMcpToast(null), 4000);
                                    return;
                                  }
                                }
                                let vaultSyncFailed = false;
                                if (vaultToken) {
                                  // The ExternalMCP row above is what the legacy LangChain chat
                                  // routes read; Managed Agents sessions (Digital Workforce chat)
                                  // instead pull a static_bearer credential from Anthropic's
                                  // vault, keyed by URL. Without this, a rotated/updated
                                  // credential only takes effect for the legacy routes and
                                  // Managed Agents keeps using the stale token until it 401s.
                                  // Best-effort: the primary save above already succeeded, so a
                                  // vault sync failure here shouldn't block it — just surface it.
                                  try {
                                    const { addMcpCredential } = await import(
                                      '../features/claudeagents/claudeAgentApi'
                                    );
                                    await addMcpCredential(mcpName.trim(), mcpUrl.trim(), vaultToken);
                                  } catch (vaultErr) {
                                    vaultSyncFailed = true;
                                  }
                                }
                                const onSuccess =
                                  feedbackPanel?.type === 'add-mcp'
                                    ? feedbackPanel.onSuccess
                                    : undefined;
                                setFeedbackPanel(null);
                                if (isOpen) onToggle();
                                onSuccess?.();
                                setMcpToast(
                                  vaultSyncFailed
                                    ? {
                                        type: 'error',
                                        message:
                                          'Server saved, but syncing the credential for Digital Workforce chat failed. Try updating it again.',
                                      }
                                    : {
                                        type: 'success',
                                        message: mcpEditData?.transactionId
                                          ? 'MCP server updated successfully!'
                                          : 'MCP server added successfully!',
                                      },
                                );
                                setTimeout(() => setMcpToast(null), vaultSyncFailed ? 5000 : 3000);
                              } catch {
                                setMcpToast({
                                  type: 'error',
                                  message:
                                    'Something went wrong. Please try again.',
                                });
                                setTimeout(() => setMcpToast(null), 3000);
                              } finally {
                                setMcpSaving(false);
                              }
                            }}
                            disabled={!mcpTrust || mcpSaving}
                            className="mcp-panel__save-btn"
                            style={{
                              cursor:
                                mcpTrust && !mcpSaving
                                  ? 'pointer'
                                  : 'not-allowed',
                              opacity: mcpTrust && !mcpSaving ? 1 : 0.45,
                            }}
                          >
                            {mcpSaving
                              ? mcpEditData?.transactionId
                                ? 'Updating…'
                                : 'Adding…'
                              : mcpEditData?.transactionId
                                ? 'Update Server'
                                : 'Add Server'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })()
          ) : showRunDetail ? (
            <RunDetailSidebarPanel />
          ) : showCallDetail ? (
            <CallDetailSidebarPanel />
          ) : showDialerCallDetail ? (
            <DialerCallDetailSidebarPanel />
          ) : showPolicyDetail ? (
            <PolicyDetailSidebarPanel
              key={selectedPolicyId!}
              policyId={selectedPolicyId!}
            />
          ) : showInsuredDetail ? (
            <InsuredDetailSidebarPanel
              key={activeInsuredId!}
              insuredId={activeInsuredId!}
            />
          ) : showCarrierSession ? (
            <CarrierSessionDetailSidebarPanel />
          ) : showStatusTemplate ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                padding: 'var(--space-1) 0',
              }}
            >
              {/* Progress bar */}
              <div className='rsp__progress-track'>
                <motion.div
                  className='rsp__progress-fill'
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
              {/* Steps */}
              <div className='rsp__steps'>
                {statusTemplate!.statusSteps.map((step) => (
                  <div
                    key={step.step}
                    className={`rsp__step rsp__step--${step.status}`}
                  >
                    <span className='rsp__step-num'>{step.step}</span>
                    <span className='rsp__step-desc'>{step.description}</span>
                    <span
                      className={`rsp-icon rsp-icon--${step.status === 'completed' ? 'completed' : step.status === 'in_progress' ? 'progress' : step.status === 'failed' ? 'failed' : 'pending'}`}
                    >
                      {step.status === 'completed' ? (
                        <FiCheck size={13} />
                      ) : step.status === 'in_progress' ? (
                        <FiRefreshCw size={13} />
                      ) : step.status === 'failed' ? (
                        <FiX size={13} />
                      ) : (
                        <svg
                          width={11}
                          height={11}
                          viewBox='0 0 12 12'
                          fill='none'
                        >
                          <circle
                            cx='6'
                            cy='6'
                            r='5'
                            stroke='currentColor'
                            strokeWidth='1.5'
                          />
                        </svg>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : showAddClawSkill ? (
            <div
              className='sidebar-right__content'
              style={{ padding: '0 16px 16px' }}
            >
              <motion.form
                className='create-amp__form'
                onSubmit={handleClawSkillSubmit}
                style={{
                  margin: 0,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  boxShadow: 'none',
                }}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div className='create-amp__fields'>
                  <div className='create-amp__field'>
                    <label className='create-amp__label'>Name</label>
                    <input
                      className='create-amp__input'
                      type='text'
                      placeholder='e.g. Carrier Portal Login'
                      value={clawSkillName}
                      onChange={(e) => setClawSkillName(e.target.value)}
                      maxLength={60}
                      required
                      autoFocus
                    />
                  </div>
                  <div className='create-amp__field'>
                    <label className='create-amp__label'>Skill</label>
                    <textarea
                      className='create-amp__input create-amp__textarea'
                      placeholder='Describe what this skill does and how it should behave...'
                      value={clawSkillContent}
                      onChange={(e) => setClawSkillContent(e.target.value)}
                      rows={18}
                      required
                    />
                  </div>
                  {clawSkillError && (
                    <p style={{ fontSize: 12, color: '#ef4444', margin: 0 }}>
                      {clawSkillError}
                    </p>
                  )}
                </div>
                <div className='create-amp__actions'>
                  <motion.button
                    type='button'
                    className='glass-btn'
                    onClick={onCloseAddClawSkill}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    className='glass-btn glass-btn-primary'
                    type='submit'
                    disabled={
                      !clawSkillName.trim() ||
                      !clawSkillContent.trim() ||
                      clawSkillCreating
                    }
                    whileHover={{ scale: !clawSkillCreating ? 1.02 : 1 }}
                    whileTap={{ scale: !clawSkillCreating ? 0.97 : 1 }}
                  >
                    <FiPlus size={15} />
                    {clawSkillCreating
                      ? editClawSkillData
                        ? 'Saving…'
                        : 'Creating…'
                      : editClawSkillData
                        ? 'Save Skill'
                        : 'Create Skill'}
                  </motion.button>
                </div>
              </motion.form>
            </div>
          ) : showOpenClawPanel ? (
            <OpenClawSidebarPanel isConnected={openClawConnected} />
          ) : showCertificates ? (
            <CertificatesSidebarPanel />
          ) : showRateQuoteLink ? (
            <RateQuoteLinkSidebarPanel />
          ) : showDocumentPanel ? (
            <DocumentPanel
              title={
                isTruckingPage
                  ? 'Upload Files for Your Appetite'
                  : 'Upload Files to Configure Your Risk Appetite'
              }
              subtitle={
                isTruckingPage
                  ? 'Add files to enhance your analysis'
                  : 'Supported formats: PDF, Images, CSV, Excel'
              }
              documents={docPanelDocs}
              loading={docPanelLoading}
              onUpload={async (files) => {
                if (isTruckingPage)
                  await truckingService.uploadDocuments(files);
                else await carrierFinderService.uploadFiles(files);
                await loadDocPanelDocs();
              }}
              onDelete={async (id) => {
                if (isTruckingPage) await truckingService.deleteDocument(id);
                else await carrierFinderService.deleteFile(id);
                await loadDocPanelDocs();
              }}
              onDownload={async (id) => {
                if (isTruckingPage) {
                  const url = await truckingService.downloadDocument(id);
                  if (url) window.open(url, '_blank');
                } else {
                  const doc = docPanelDocs.find((d) => d.id === id);
                  if (doc && (doc as any).fileUrl)
                    window.open((doc as any).fileUrl, '_blank');
                }
              }}
              onClose={() => setShowDocPanel(false)}
            />
          ) : showKanbanConfig ? (
            <CommandCenterConfigSidebarPanel />
          ) : isMacWorkflowsPage ? (
            <MacWorkflowsSidebarContent />
          ) : isArtifactsPage ? (
            <ArtifactsSidebarContent />
          ) : isDwArtifactsPage ? (
            <DwAgentDashboardSidebarContent />
          ) : isRoutinesPage ? (
            <RoutinesSidebarContent />
          ) : isCarrierDownloadPage ? (
            <CarrierDownloadSidebarContent />
          ) : isVoicePage ? (
            <VoiceReportsSidebar />
          ) : isDialerPage ? (
            <DialerSidebarContent />
          ) : isCommunicationCenterPage ? (
            <CommunicationSidebarContent />
          ) : isSubmissionCenterPage ? (
            <SubmissionCenterSidebarContent />
          ) : isWebhooksPage ? (
            webhookCallId
              ? <WebhookCallDetailSidebar key={webhookCallId} callId={webhookCallId} onBack={closeWebhookCall} />
              : <WebhookAnalyticsSidebarContent />
          ) : !isAuthenticated ? (
            <UpgradeBanner feature='analytics dashboard' />
          ) : isOpen ? (
            // Reports Dashboard is temporarily hidden (and its APIs not called)
            // per request — component below intentionally not rendered for now.
            // <DashboardWidgets />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                height: "100%",
                padding: "24px 20px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "rgba(162,0,255,0.12)",
                  border: "1px solid rgba(162,0,255,0.28)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#a200ff",
                }}
              >
                <FiBarChart2 size={19} />
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.2px" }}>
                Coming Soon
              </span>
              <span style={{ fontSize: 12, color: "var(--text-tertiary)", lineHeight: 1.6, maxWidth: 220 }}>
                Reports Dashboard is temporarily unavailable. Check back soon.
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {mcpToast && (
        <motion.div
          className={
            mcpToast.type === 'success' ? 'success-toast' : 'error-toast'
          }
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.25 }}
        >
          {mcpToast.type === 'success' ? (
            <FiCheck size={16} />
          ) : (
            <FiAlertTriangle size={16} />
          )}
          <span>{mcpToast.message}</span>
        </motion.div>
      )}
    </aside>
  );
};

export default SidebarRight;
